import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Paper,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Group as GroupIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import {
  getPaginatedUsers,
  bulkAssignTherapistsToSupervisors,
  removeTherapistFromSupervisor
} from '../../../services/api';

function AssignTherapistsModal({ open, supervisor, onClose, onSuccess, token }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [therapists, setTherapists] = useState([]);
  const [selectedTherapistIds, setSelectedTherapistIds] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // Currently assigned therapists
  const [assignedTherapists, setAssignedTherapists] = useState([]);

  useEffect(() => {
    if (open && supervisor) {
      loadUnassignedTherapists();
      setAssignedTherapists(supervisor.supervisorInfo?.assignedTherapists || []);
      setSelectedTherapistIds([]);
      setError('');
    }
  }, [open, supervisor, page, rowsPerPage, searchQuery]);

  const loadUnassignedTherapists = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        role: 'therapist',
        sortBy: 'name',
        sortOrder: 'asc'
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      const data = await getPaginatedUsers(params, token);

      // Filter to only therapists not already assigned to this supervisor
      const unassignedTherapists = (data.users || []).filter(therapist => {
        const isAlreadyAssigned = therapist.therapistInfo?.assignedSupervisors?.some(
          sup => (sup._id || sup).toString() === supervisor._id.toString()
        );
        return !isAlreadyAssigned;
      });

      setTherapists(unassignedTherapists);
      setTotalCount(data.pagination?.total || 0);
    } catch (err) {
      console.error('Error loading therapists:', err);
      setError(err.message || 'Failed to load therapists');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedTherapistIds(therapists.map(t => t._id));
    } else {
      setSelectedTherapistIds([]);
    }
  };

  const handleSelectOne = (therapistId) => {
    setSelectedTherapistIds(prev => {
      if (prev.includes(therapistId)) {
        return prev.filter(id => id !== therapistId);
      } else {
        return [...prev, therapistId];
      }
    });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleAssign = async () => {
    if (selectedTherapistIds.length === 0) {
      setError('Please select at least one therapist');
      return;
    }

    // Check capacity
    const currentCount = assignedTherapists.length;
    const maxCapacity = supervisor.supervisorInfo?.maxTherapistCapacity || 10;
    const availableSlots = maxCapacity - currentCount;

    if (selectedTherapistIds.length > availableSlots) {
      setError(`Supervisor only has ${availableSlots} available slots. Selected ${selectedTherapistIds.length} therapists.`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      await bulkAssignTherapistsToSupervisors(selectedTherapistIds, [supervisor._id], token);
      onSuccess();
    } catch (err) {
      console.error('Error assigning therapists:', err);
      setError(err.message || 'Failed to assign therapists');
      setLoading(false);
    }
  };

  const handleRemoveTherapist = async (therapistId) => {
    try {
      setLoading(true);
      setError('');
      await removeTherapistFromSupervisor(supervisor._id, therapistId, token);

      // Update local state
      setAssignedTherapists(prev => prev.filter(t => (t._id || t).toString() !== therapistId.toString()));

      // Refresh the list
      await loadUnassignedTherapists();
      onSuccess();
    } catch (err) {
      console.error('Error removing therapist:', err);
      setError(err.message || 'Failed to remove therapist');
    } finally {
      setLoading(false);
    }
  };

  if (!supervisor) return null;

  const currentCount = assignedTherapists.length;
  const maxCapacity = supervisor.supervisorInfo?.maxTherapistCapacity || 10;
  const availableSlots = maxCapacity - currentCount;
  const wouldExceedCapacity = selectedTherapistIds.length > availableSlots;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon />
          Manage Therapists for {supervisor.name}
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Supervisor Info Card */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Current Therapists</Typography>
              <Typography variant="h6">
                {currentCount} / {maxCapacity}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Available Slots</Typography>
              <Typography variant="h6" color={availableSlots > 0 ? 'success.main' : 'error.main'}>
                {availableSlots}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Selected to Add</Typography>
              <Typography variant="h6" color={wouldExceedCapacity ? 'error.main' : 'primary.main'}>
                {selectedTherapistIds.length}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {wouldExceedCapacity && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Warning: Selected {selectedTherapistIds.length} therapists exceeds available capacity of {availableSlots} slots.
          </Alert>
        )}

        {/* Currently Assigned Therapists */}
        {assignedTherapists.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Currently Assigned Therapists
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {assignedTherapists.map((therapist) => (
                  <Chip
                    key={therapist._id || therapist}
                    label={therapist.name || therapist}
                    onDelete={() => handleRemoveTherapist(therapist._id || therapist)}
                    deleteIcon={
                      <Tooltip title="Remove from supervisor">
                        <DeleteIcon />
                      </Tooltip>
                    }
                  />
                ))}
              </Box>
            </Paper>
          </Box>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Add New Therapists
        </Typography>

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          label="Search therapists"
          placeholder="Name or email..."
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Therapists Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedTherapistIds.length > 0 && selectedTherapistIds.length < therapists.length}
                    checked={therapists.length > 0 && selectedTherapistIds.length === therapists.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Clients</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : therapists.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {searchQuery ? 'No unassigned therapists found matching search' : 'No unassigned therapists available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                therapists.map((therapist) => (
                  <TableRow
                    key={therapist._id}
                    hover
                    onClick={() => handleSelectOne(therapist._id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedTherapistIds.includes(therapist._id)} />
                    </TableCell>
                    <TableCell>{therapist.name}</TableCell>
                    <TableCell>{therapist.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${therapist.therapistInfo?.assignedClientCount || 0}/${therapist.therapistInfo?.maxClientCapacity || 20}`}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={therapist.status}
                        size="small"
                        color={therapist.status === 'Active' ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={loading || selectedTherapistIds.length === 0 || wouldExceedCapacity}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          {loading ? 'Assigning...' : `Assign ${selectedTherapistIds.length} Therapist${selectedTherapistIds.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssignTherapistsModal;
